import React from 'react';
import { MultipleFileSelectorField, DEFAULT_REQUIRED_MESSAGE, EMAIL_INVALID_MESSAGE,
 URL_INVALID_MESSAGE, PHONE_INVALID_MESSAGE } from 'common/components/form_fields';
import { PAPTextInputField, PAPSelectInputField, PAPTextAreaField, PAPRadioInputField }
  from 'pap/components/common/input_fields/basic';
import GridSelectorInput from 'pap/components/common/input_fields/grid_selector';
import MultiSelectorInput from 'pap/components/common/input_fields/multi_selector';
import ColorPaletteSelectorInput from 'pap/components/common/input_fields/color_selector';
import PackageSelectorInput from 'pap/containers/common/input_fields/package_selector';
import { PAPItemListInput } from 'pap/components/common/input_fields/item_list';
import { FeatureSelector, BinarySelector } from 'pap/components/common/input_fields/feature_selector';
import Contract from 'pap/containers/common/input_fields/contract';
import LazyRegistration from 'pap/containers/common/input_fields/lazy_registration';
import Budget1to1 from 'pap/components/common/input_fields/budget_1_1';
import { ProjectOptions, PromotionOptions, ProtectionOptions, AwardOptions } from
  'pap/components/common/input_fields/package_options';
import SliderSelector from 'pap/components/common/input_fields/slider_selector';
import idx from 'idx';
import keys from 'lodash/keys';
import isEmail from 'sane-email-validation';
import { isPhoneValid } from 'common/utils';
import { isDiscountFeature } from 'pap/utils';
import { PACKAGE_ITEMS } from 'pap/ducks/workflow';

/* VALIDATION FUNCTIONS USED IN PAP WORKFLOW
------------------------------------------------------------------------------------------------ */
const URL_REGEXP = new RegExp(
  "^" +
    // protocol identifier
    "(?:(?:https?|ftp)://)*" +
    // user:pass authentication
    "(?:\\S+(?::\\S*)?@)?" +
    "(?:" +
      // IP address exclusion
      // private & local networks
      "(?!(?:10|127)(?:\\.\\d{1,3}){3})" +
      "(?!(?:169\\.254|192\\.168)(?:\\.\\d{1,3}){2})" +
      "(?!172\\.(?:1[6-9]|2\\d|3[0-1])(?:\\.\\d{1,3}){2})" +
      // IP address dotted notation octets
      // excludes loopback network 0.0.0.0
      // excludes reserved space >= 224.0.0.0
      // excludes network & broacast addresses
      // (first & last IP address of each class)
      "(?:[1-9]\\d?|1\\d\\d|2[01]\\d|22[0-3])" +
      "(?:\\.(?:1?\\d{1,2}|2[0-4]\\d|25[0-5])){2}" +
      "(?:\\.(?:[1-9]\\d?|1\\d\\d|2[0-4]\\d|25[0-4]))" +
    "|" +
      // host name
      "(?:(?:[a-z\\u00a1-\\uffff0-9]-*)*[a-z\\u00a1-\\uffff0-9]+)" +
      // domain name
      "(?:\\.(?:[a-z\\u00a1-\\uffff0-9]-*)*[a-z\\u00a1-\\uffff0-9]+)*" +
      // TLD identifier
      "(?:\\.(?:[a-z\\u00a1-\\uffff]{2,}))" +
      // TLD may end with dot
      "\\.?" +
    ")" +
    // port number
    "(?::\\d{2,5})?" +
    // resource path
    "(?:[/?#]\\S*)?" +
  "$", "i"
);

export const required = value => {
  if (value instanceof Array) {
    return value && value.length > 0 ? undefined : DEFAULT_REQUIRED_MESSAGE;
  } else {
    return value ? undefined : DEFAULT_REQUIRED_MESSAGE;
  }
}

export const requiredConfirmation = value => {
  if (!value) {
    return "You must check the box to continue.";
  }
}

export const phone = value => {
  if (!isPhoneValid(value)) {
    return PHONE_INVALID_MESSAGE;
  }
}

const url = value => (value && value.length > 0) ? (URL_REGEXP.test(value) ? undefined : URL_INVALID_MESSAGE) : undefined

const allBinaryRequired = (value, allValues, props) => {
  const questions = props.briefQuestions || props.questions || [];
  const field = questions.find(q => q.name === props.stepName);

  if (!value || !field.options || value.length !== field.options.length) {
    return "Please make a selection";
  }
}

const validateProjectTitle = (value) => {
  const curLength = `Your title is currently ${value.length} character(s).`;
  const invalidLengthMsg =
    `${curLength}  Please use between 15 and 100 characters (including spaces).`;
  return (value && value.length >= 15 && value.length <= 100)
    ? undefined
    : invalidLengthMsg;
};

const validateAwards = (value, allValues, {selectedPackage, projectData, downgradeForbidden}) => {
  /* no package or project to validate awards against */
  if (!selectedPackage && !projectData) {
    return undefined
  }

  /* make sure extra awards respect the minimum value */
  const fieldValues = (value || {})['extra_awards'] || []
  const awards = (
    selectedPackage && (!projectData || selectedPackage.id != projectData['original_package'])?
    selectedPackage.awards : projectData['award_rules']
  )
  let errors = []

  let referenceAward = awards.find((a) => a.feature.position == 0)
  referenceAward = referenceAward? referenceAward.feature : null

  fieldValues.filter(v => !v.deleted).map((v) => {
    const award = v.uuid? awards.find((a) => a.uuid == v.uuid).feature : referenceAward;

    /* enforce minimum value */
    if (v.value < award.min) {
      errors.push({description: `Award value must be at least $${award.min}`, value: v.value})

    /* enforce increments */
    } else if (v.value % 5 !== 0) {
      errors.push({description: "Award value must be a multiple of $5", value: v.value})

    /* enforce downgrade forbidden */
    } else if (downgradeForbidden && v.value < award.value) {
      errors.push({description: "Award value can't be decreased", value: v.value})
    }
  })
  if (errors.length) {
    return {extra_awards: errors}
  }
  return undefined
}

const validateContract = (value={}) => {
  /* make sure all necessary contract info is provided */
  let error = undefined
  if (value.isCustom) {
    if (!value['custom_contract']) {
      error = "Please upload a custom contract PDF"
    }
  } else {
    if (!value['client_legal_name'] || !value['client_legal_country']) {
      error =  "Please provide both legal name and country"
    }
  }
  return error
}

const validate1to1Budget = (value={}, allValues, {selectedPackage, projectData}) => {
  /* no package or project to validate budget against */
  if (!selectedPackage && !projectData) {
    return undefined
  }

  /* make sure one of the options is selected */
  if (!value['budget_option']) {
    return {"budget_option": "Please select one option"}
  }

  if (value['budget_option'] == 'send_offer') {
    const award = value['award_1_1'] || 0
    const awards = (
      selectedPackage && (!projectData || selectedPackage.id != projectData['original_package'])?
      selectedPackage.awards : projectData['award_rules']
    )
    const referenceAward = awards.find((a) => a.feature.position == 0).feature
    const minAward = parseFloat(referenceAward.min || 0)

    /* enforce minimum award */
    if (award < minAward) {
      return {"award_1_1": `Award value must be at least $${minAward}`}
    }

    /* enforce integers */
    if (award % 1 !== 0) {
      return {"award_1_1": "Please use whole numbers without decimals"}
    }
  }
}

const validateLazyRegistration = (value={}) => {
  const errors = {};

  /* validate email */
  if (!value.email) {
    errors.email = DEFAULT_REQUIRED_MESSAGE;
  } else if (!isEmail(value.email)) {
    errors.email = EMAIL_INVALID_MESSAGE;
  }

  return Object.keys(errors).length > 0? errors : undefined;
}

/* Validate project fields that can be changed once features are activated. */
const validateFeatureProjectFields = (value={}, _, { selectedPackage, projectData, stepName, user }) => {
  if (!selectedPackage && !projectData) {
    return undefined;
  }

  const features = (
    selectedPackage && (!projectData || selectedPackage.id !== projectData.original_package)?
    selectedPackage.features : projectData.features
  );
  const billables = projectData? projectData.billables : null;
  const items = stepName? PACKAGE_ITEMS.filter((f) => f.step === stepName) : PACKAGE_ITEMS;
  const errors = {};

  items.forEach(item => {
    let feature = (features || []).find((f) => f.feature.name === item.name);

    if (!feature || !item.projectFields) {
      return;
    }

    const isDiscount = isDiscountFeature(
      feature, features, billables, idx(user, _ => _.profile_data)
    );
    feature = feature.feature;
    const selected = (
      isDiscount || (value[item.fieldName] === undefined ? feature.active : value[item.fieldName])
    );

    /* only validate if feature is activated */
    if (!selected) {
      return;
    }

    item.projectFields.forEach(field => {
      if (!field.validate) {
        return;
      }

      /* value is determined according to the following precedence order:
         form (dirty) value -> project field value -> default value */
      let fieldValue = field.defaultValue;
      if (value[field.name] !== undefined) {
        fieldValue = value[field.name];
      } else if (projectData) {
        if (field.type === 'brief_question') {
          fieldValue = projectData.brief_answers[field.name];
        } else {
          fieldValue = projectData[field.name];
        }
      }

      const error = field.validate(fieldValue);

      if (!error) {
        return;
      }

      errors[field.name] = error;
    })
  })

  return keys(errors).length > 0 ? errors : undefined;
}


/* Maps field type identifiers to actual components. Used to display the right components based on
 * the configuration sent by the server.
 */
export const PAP_INPUT_COMPONENTS_MAP = {
  "text": PAPTextInputField,
  "url": PAPTextInputField,
  "textarea": PAPTextAreaField,
  "textarea_html": PAPTextAreaField,
  "select": PAPSelectInputField,
  "radio": PAPRadioInputField,
  "item-list": PAPItemListInput,
  "multi-grid-select": GridSelectorInput,
  "multi-select": MultiSelectorInput,
  "color-selector": ColorPaletteSelectorInput,
  "files": MultipleFileSelectorField,
  "packages": PackageSelectorInput,
  "award_options": AwardOptions,
  "project_options": ProjectOptions,
  "protection_options": ProtectionOptions,
  "promotion_options": PromotionOptions,
  "lazy-registration": LazyRegistration,
  "feature-select": FeatureSelector,
  "binary-select": BinarySelector,
  "contract": Contract,
  "budget": Budget1to1,
  "slider-select": SliderSelector
};

/* Maps validation type identifiers to actual components.
*/
export const PAP_VALIDATION_FUNCTIONS_MAP = {
  "required": required,
  "url": url,
  "allBinaryRequired": allBinaryRequired,
  "awards": validateAwards,
  "contract": validateContract,
  "lazyRegistration": validateLazyRegistration,
  "budget": validate1to1Budget,
  "projectTitle": validateProjectTitle,
  "featureProjectFields": validateFeatureProjectFields
};



// WEBPACK FOOTER //
// ./pap/components/common/input_fields/index.jsx